import React, {useCallback, useState} from "react"
import {useTranslation} from "react-i18next"
import {Navigate, useNavigate} from "react-router-dom"
import {useConfig} from "../../hooks/useConfig"
import {useBoothSession} from "../../hooks/useBoothSession"
import {Box, Fade, FormControl, MenuItem, Select, Stack, TextField, Typography} from "@mui/material"
import {AiEffectSequenceGuestPickerInputResource} from "../../library/models/AiEffectSequenceResource"
import {ArrowForwardOutlined} from "@mui/icons-material"
import BigButton from "../buttons/BigButton"
import {floatingInputSx} from "../../styles/inputStyles"

function GuestInputPage() {
  const navigate = useNavigate()
  const {t} = useTranslation()

  const config = useConfig()
  const {rawImage, guestInputValues, setGuestInputValues} = useBoothSession()

  const guestInputs = config?.aiEffectSequence?._embedded.guestInputs ?? []
  const sortedInputs = [...guestInputs].sort((a, b) => a.order - b.order)

  const [values, setValues] = useState<Record<string, string>>(guestInputValues)

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({...prev, [key]: value}))
  }

  const handleSubmit = useCallback(() => {
    setGuestInputValues(values)
    navigate(`/${config.presetCode}/photo-processing`)
  }, [config, values, setGuestInputValues, navigate])

  if (!rawImage) {
    return (
      <Navigate
        to={`/${config.presetCode}/camera`}
        replace
      />
    )
  }

  const theme = config.theme

  return (
    <Fade
      in
      timeout={500}
    >
      <Stack
        spacing={3}
        justifySelf="center"
        alignItems="flex-start"
        sx={{
          width: "100%",
          maxWidth: 600,
          mt: 5,
        }}
      >

        <Stack direction="row" spacing={2} alignItems="flex-end">
          <Box
            component="img"
            src={rawImage}
            alt=""
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              objectFit: "cover",
            }}
          />
          <Typography
            variant="h2"
            fontSize="1.85rem"
            fontWeight={600}
            color={config.theme.textColor}
          >
            {t("guestInput.heading")}
          </Typography>
        </Stack>

        <Stack alignItems="flex-start" spacing={2} sx={{width: 1}}>
          {sortedInputs.map((input) => {
            const key = input.promptPlaceholder

            if (input.type === "TEXT") {
              return (
                <Box key={key} sx={{width: 1}}>
                  <Typography sx={{mb: 0.5, fontWeight: 500, textAlign: "left", color: theme.textColor}}>
                    {input.label}
                  </Typography>
                  <TextField
                    value={values[key] ?? ""}
                    onChange={(e) => handleChange(key, e.target.value)}
                    placeholder={input.defaultValue || input.label}
                    fullWidth
                    sx={floatingInputSx}
                  />
                </Box>
              )
            }

            const pickerInput = input as AiEffectSequenceGuestPickerInputResource
            const sortedOptions = [...pickerInput._embedded.options].sort(
              (a, b) => a.order - b.order,
            )

            return (
              <Box key={key} sx={{width: 1}}>
                <Typography sx={{mb: 0.5, fontWeight: 500, textAlign: "left", color: theme.textColor}}>
                  {input.label}
                </Typography>
                <FormControl fullWidth sx={floatingInputSx}>
                  <Select
                    value={values[key] ?? ""}
                    onChange={(e) => handleChange(key, e.target.value as string)}
                    displayEmpty
                    MenuProps={{style: {zIndex: 9999}}}
                    sx={{textAlign: "left"}}
                  >
                    {sortedOptions.map((option) => (
                      <MenuItem
                        key={option.id}
                        value={option.value}
                      >
                        {option.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )
          })}
        </Stack>

        <Stack direction="row" spacing={3} sx={{width: 1}}>
          <BigButton
            variant="outlined"
            onClick={() => navigate(`/${config.presetCode}/camera`)}
            sx={{flex: 1, minWidth: 0}}
          >
            {t("common.cancel")}
          </BigButton>
          <BigButton
            onClick={handleSubmit}
            disabled={sortedInputs.some((input) => !values[input.promptPlaceholder]?.trim())}
            sx={{flex: 1, minWidth: 0}}
          >
            {t("guestInput.submit")}
            <ArrowForwardOutlined sx={{ml: 1}}/>
          </BigButton>
        </Stack>
      </Stack>
    </Fade>
  )
}

export default GuestInputPage
